/**
 * MemorialShareBadge — the "share badge" panel for a memorial page.
 *
 * Big Splink QR (200 × 200), the short URL, and every practical way to
 * pass the memorial along:
 *   • WhatsApp   • Facebook   • X (Twitter)   • LinkedIn
 *   • Email       • SMS         • Copy link      • Native share (mobile)
 *
 * Rendered in the right column of the memorial hero so the previously
 * empty whitespace fills evenly with the left column.
 *
 * Props:
 *   memorial: { slug, id, full_name, share_short_url?, qr_code_url? }
 *   fallbackShareUrl: canonical URL to use when the memorial doesn't
 *                     yet have a Splink short URL (rare — backfilled).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Copy, Check, MessageCircle, Mail, Share2,
  Send, Facebook, Linkedin, Twitter, Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";

export default function MemorialShareBadge({ memorial, fallbackShareUrl, onPreview }) {
  const [copied, setCopied] = useState(false);
  const [remote, setRemote] = useState(null); // fetched short_url + qr if not on memorial

  // Try to hydrate from the memorial doc first; else pull from
  // /beautify GET (public, no LLM call — just reads what's stored).
  useEffect(() => {
    if (!memorial?.slug) return;
    if (memorial.share_short_url && memorial.qr_code_url) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get(`/memorials/${memorial.slug}/beautify`);
        if (!cancelled && (data?.share_short_url || data?.qr_code_url)) {
          setRemote({ share_short_url: data.share_short_url, qr_code_url: data.qr_code_url });
        }
      } catch (_) { /* not fatal */ }
    })();
    return () => { cancelled = true; };
  }, [memorial?.slug, memorial?.share_short_url, memorial?.qr_code_url]);

  const shortUrl = memorial?.share_short_url || remote?.share_short_url || fallbackShareUrl;
  const qrUrl    = memorial?.qr_code_url     || remote?.qr_code_url;
  const name     = memorial?.full_name || "Beloved";

  // Cache the text once — same across all channels.
  const shareText = useMemo(
    () => `Remembering ${name} · IndianFamilyTree.com`,
    [name],
  );
  const url = shortUrl || "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const nativeShare = async () => {
    const data = { title: shareText, text: shareText, url };
    if (navigator.share) {
      try { await navigator.share(data); } catch { /* dismissed */ }
    } else {
      copyLink();
    }
  };

  // Channel-specific deep links. Every one opens in a new tab.
  const channels = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      color: "#25D366",
      Icon: MessageCircle,
      href: `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${url}`)}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      color: "#1877F2",
      Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
    {
      key: "x",
      label: "X",
      color: "#000000",
      Icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      color: "#0A66C2",
      Icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    },
    {
      key: "email",
      label: "Email",
      color: "#B45309",
      Icon: Mail,
      href: `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(`${shareText}\n\n${url}`)}`,
    },
    {
      key: "sms",
      label: "SMS",
      color: "#0EA5E9",
      Icon: Smartphone,
      href: `sms:?body=${encodeURIComponent(`${shareText}\n${url}`)}`,
    },
    {
      key: "telegram",
      label: "Telegram",
      color: "#229ED9",
      Icon: Send,
      href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`,
    },
  ];

  return (
    <section
      className="rounded-2xl p-5 md:p-6 border"
      data-testid="memorial-share-badge"
      style={{
        background: "linear-gradient(180deg, #FFF8EC 0%, #FBE9C6 100%)",
        borderColor: "rgba(122,74,31,0.22)",
        boxShadow: "0 12px 30px -20px rgba(122,74,31,0.35)",
      }}
    >
      <h3
        className="font-display leading-tight"
        style={{ color: "#3B1F0A", fontSize: "clamp(1.85rem, 3.4vw, 2.75rem)" }}
      >
        A scan or a tap brings loved ones back to leave a tribute.
      </h3>

      <div className="mt-4 flex flex-col gap-4">
        {/* Big QR — centered above the share buttons */}
        <div
          className="flex-shrink-0 rounded-xl p-3 border mx-auto"
          style={{ background: "#FFFFFF", borderColor: "rgba(122,74,31,0.22)" }}
        >
          {qrUrl ? (
            <img
              src={qrUrl}
              alt={`Scan to open the memorial of ${name}`}
              className="block w-[192px] h-[192px]"
              data-testid="memorial-share-qr"
              loading="lazy"
            />
          ) : (
            <div
              className="w-[192px] h-[192px] flex items-center justify-center text-xs"
              style={{ color: "#7A4A1F" }}
              data-testid="memorial-share-qr-empty"
            >
              QR generating…
            </div>
          )}
          <div className="mt-2 text-center">
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#7A4A1F" }}>
              Scan · Open · Tribute
            </div>
            <div className="text-[11px] font-mono mt-0.5 break-all" style={{ color: "#3B1F0A" }}>
              {(shortUrl || "").replace(/^https?:\/\//, "")}
            </div>
          </div>
        </div>

        {/* Share buttons — full-width 3-column grid below the QR. */}
        <div className="w-full">
          <div className="grid grid-cols-3 gap-2">
            {channels.map((c) => (
              <a
                key={c.key}
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`share-${c.key}`}
                className="flex items-center gap-2 rounded-full px-3 py-2.5 text-sm font-semibold transition-transform hover:-translate-y-[1px] hover:shadow-sm border"
                style={{
                  borderColor: c.color,
                  color: c.color,
                  background: "#FFFFFF",
                }}
              >
                <c.Icon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{c.label}</span>
              </a>
            ))}
            <button
              type="button"
              onClick={copyLink}
              data-testid="share-copy"
              className="flex items-center gap-2 rounded-full px-3 py-2.5 text-sm font-semibold transition-transform hover:-translate-y-[1px] hover:shadow-sm border"
              style={{ borderColor: "#7A4A1F", color: "#7A4A1F", background: "#FFFFFF" }}
            >
              {copied ? <Check className="w-4 h-4 flex-shrink-0" /> : <Copy className="w-4 h-4 flex-shrink-0" />}
              <span className="truncate">{copied ? "Copied" : "Copy link"}</span>
            </button>
            <button
              type="button"
              onClick={nativeShare}
              data-testid="share-native"
              className="flex items-center gap-2 rounded-full px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-[1px]"
              style={{ background: "#C2410C" }}
            >
              <Share2 className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">More…</span>
            </button>
          </div>

          <div className="mt-3 text-[11px]" style={{ color: "#7A4A1F" }}>
            Every share is trackable through Splink so the family can see
            how many people have visited &amp; scanned the badge.
          </div>
        </div>
      </div>
    </section>
  );
}
